import React from 'react'
import intl from 'react-intl-universal'
import { Button } from 'reactstrap'

import { EllipsisReduxStore } from '~/types'
import Tabs from '~/reactapp/shared/Tabs'

import { LineForm, useLineForm } from './LineForm'
import LineConfigurationModal from './LineConfigurationModal'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('./LineConfiguration.styles.scss')

type Props = Pick<EllipsisReduxStore['settings'], 'lineSettings'> & {
  onChange: (lineConfig: LineConfigWithWebsite) => void
  onDelete: (website: string) => void
  websites: React.ComponentProps<typeof LineConfigurationModal>['websites']
}

type LineConfigWithWebsite = React.ComponentProps<
  typeof LineConfigurationModal
>['initialLineConfig']

function serializeValues(lineConfig: LineConfigWithWebsite) {
  return {
    ...lineConfig,
    value: Number(lineConfig.value),
    linesPerInch: Number(lineConfig.linesPerInch),
  }
}

export default function LineConfiguration(props: Props) {
  const [activeTabIndex, setActiveTabIndex] = React.useState(0)
  const [modalInitialLineConfig, setModalInitialLineConfig] = React.useState<
    LineConfigWithWebsite
  >()
  const lineForm = useLineForm(props.lineSettings.default)

  const validateDefaultFields = () => {
    const isValid = lineForm.validate()
    if (isValid) {
      props.onChange(
        serializeValues({ ...lineForm.fields, website: 'default' })
      )
    }
  }

  const websiteEntries = Object.entries(props.lineSettings.websites)

  const Default = (
    <React.Fragment>
      <p>{intl.get('LineConfiguration.tabs.default.description')}</p>
      <LineForm
        {...lineForm}
        // Update settings object when text fields are blurred
        onBlur={validateDefaultFields}
        handleFieldChange={(event) => {
          lineForm.handleFieldChange(event)

          // Update settings object when radio value changes
          const { type, value } = event.currentTarget
          if (type === 'radio') {
            props.onChange(
              serializeValues({
                ...lineForm.fields,
                unit: value as 'words' | 'chars',
                website: 'default',
              })
            )
          }
        }}
      />
    </React.Fragment>
  )

  return (
    <React.Fragment>
      <div className={`card mb-2 ${styles.root}`}>
        <h2 className="card-header">{intl.get('LineConfiguration.title')}</h2>

        <div className="content">
          {props.websites.length === 1 ? (
            Default
          ) : (
            <Tabs
              activeTabIndex={activeTabIndex}
              onSelect={setActiveTabIndex}
              contentClass="tabs-content"
            >
              <Tabs.Tab
                title={intl.get('LineConfiguration.tabs.default.tabTitle')}
              >
                {Default}
              </Tabs.Tab>

              <Tabs.Tab
                title={intl.get('LineConfiguration.tabs.custom.tabTitle')}
              >
                <div className="custom-wrapper">
                  <Button
                    className="add-button"
                    color="primary"
                    onClick={() => {
                      setModalInitialLineConfig({
                        linesPerInch: null,
                        unit: 'words',
                        value: null,
                        website: '',
                      })
                    }}
                  >
                    {intl.get('LineConfiguration.tabs.custom.addButton')}
                  </Button>
                  {websiteEntries.length ? (
                    <React.Fragment>
                      <p>
                        {intl.get('LineConfiguration.tabs.custom.description')}
                      </p>
                      <div className="grid-container">
                        <span className="left">
                          <strong>
                            {intl.get(
                              'LineConfiguration.tabs.custom.headers.website'
                            )}
                          </strong>
                        </span>
                        <span>
                          <strong>
                            {intl.get(
                              'LineConfiguration.tabs.custom.headers.typeLine'
                            )}
                          </strong>
                        </span>
                        <span>
                          <strong>
                            {intl.get(
                              'LineConfiguration.tabs.custom.headers.linesInch'
                            )}
                          </strong>
                        </span>
                        {/* Empty header cells */}
                        <span />
                        <span />
                        {websiteEntries.map(([websiteName, lineConfig]) => {
                          return (
                            <React.Fragment key={websiteName}>
                              <span className="left text-truncate">
                                {websiteName}
                              </span>
                              <span>{lineConfig.value}</span>
                              <span>{lineConfig.linesPerInch}</span>
                              <button
                                data-testid={`btn-edit-${websiteName}`}
                                onClick={() => {
                                  setModalInitialLineConfig({
                                    ...lineConfig,
                                    website: websiteName,
                                  })
                                }}
                              >
                                <i className="fa fa-gear" />
                              </button>
                              <button
                                data-testid={`btn-delete-${websiteName}`}
                                onClick={() => {
                                  props.onDelete(websiteName)
                                }}
                              >
                                <i className="fa fa-trash-o" />
                              </button>
                            </React.Fragment>
                          )
                        })}
                      </div>
                    </React.Fragment>
                  ) : (
                    <p>
                      {intl.get(
                        'LineConfiguration.tabs.custom.noDataDescription'
                      )}
                    </p>
                  )}
                </div>
              </Tabs.Tab>
            </Tabs>
          )}
        </div>
      </div>

      {modalInitialLineConfig && (
        <LineConfigurationModal
          initialLineConfig={modalInitialLineConfig}
          toggle={() => setModalInitialLineConfig(undefined)}
          websites={props.websites}
          onSubmit={(lineConfig) => {
            props.onChange(serializeValues(lineConfig))
          }}
        />
      )}
    </React.Fragment>
  )
}
